import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Menu, X, MapPin, Filter, Search, ChevronLeft, ChevronRight, Map as MapIcon, Layers, Sun, Moon, MessageSquare, Maximize2, Camera, Info, RotateCcw, Bug } from 'lucide-react';
import MapWithEvents from './MapWithEvents';
import FeedbackModal from './FeedbackModal';
import ErrorReportModal from './ErrorReportModal';
import { FlashMessage } from './FlashMessage';
import { IntroModal } from './IntroModal';
import { WelcomeModal } from './WelcomeModal';
import { StarRating } from './StarRating';
import { useEvents } from '../../hooks/useEvents';
import { useTheme } from '../../context/ThemeContext';
import { getSignedUrlOnDemand } from '../../utils/signedUrls';
import { MUNICIPIOS_REGIAO } from '../../utils/municipios';
import { getEventColor } from '../../utils/categoryIcons';
import { analytics } from '../../services/analyticsTracker';
import logo from '../../logo.png';

const MapaCulturalPiaui = () => {
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('event');

  // View Model / Custom Hook para eventos públicos
  const {
    events: eventos,
    allEvents,
    filters,
    updateFilter,
    availableCategories,
    availableCounties,
    loading,
    updateEventLocally
  } = useEvents();

  // Estados Locais de View (UI)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // Estados para controle de Zoom e Restauração de Visão Panorâmica
  const [resetViewTrigger, setResetViewTrigger] = useState(0);
  const [currentMapZoom, setCurrentMapZoom] = useState(9);
  const [initialMapZoom, setInitialMapZoom] = useState(9);

  // Considera zoom grande quando estiver acima do zoom inicial ou quando um ponto cultural estiver selecionado
  const isZoomed = currentMapZoom > (initialMapZoom || 9) + 0.5 || Boolean(selectedEvent);

  const handleResetZoom = () => {
    if (selectedEvent) {
      setSelectedEvent(null);
    }
    if (filters.municipio && filters.municipio !== 'Todos') {
      updateFilter('municipio', 'Todos');
    }
    setResetViewTrigger(prev => prev + 1);
    analytics.trackMapNavigation('reset_zoom', { previous_zoom: currentMapZoom });
  };

  // Estado para Ampliação de Mídia (Lightbox)
  const [isImageLightboxOpen, setIsImageLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxTouchStart, setLightboxTouchStart] = useState(null);
  const [lightboxTouchEnd, setLightboxTouchEnd] = useState(null);

  const [mapType, setMapType] = useState(() => localStorage.getItem('mapPreference') || 'satellite');
  const [showIntro, setShowIntro] = useState(() => !eventIdParam && !localStorage.getItem('hasVisited'));
  const [showWelcome, setShowWelcome] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [errorReportOpen, setErrorReportOpen] = useState(false);
  const [errorReportContext, setErrorReportContext] = useState('');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [mediaAspectRatio, setMediaAspectRatio] = useState(16 / 9);
  const [outOfBoundsIndicators, setOutOfBoundsIndicators] = useState([]);

  // Estado local para input de busca com debounce de alta performance
  const [searchInputValue, setSearchInputValue] = useState(filters.searchTerm || '');

  // Sincroniza estado caso o filtro seja alterado externamente
  useEffect(() => {
    setSearchInputValue(filters.searchTerm || '');
  }, [filters.searchTerm]);

  // Debounce para aplicação do filtro de busca e telemetria
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInputValue !== filters.searchTerm) {
        updateFilter('searchTerm', searchInputValue);
        analytics.trackSearch(searchInputValue, eventos.length);
      }
    }, 150);

    return () => clearTimeout(handler);
  }, [searchInputValue, filters.searchTerm, updateFilter, eventos.length]);

  // Armazenar os links originais extraídos (que serão convertidos onDemand)
  const [resolvedMediaUrls, setResolvedMediaUrls] = useState({});

  // Telemetria: Registro de acesso inicial e cálculo de tempo de navegação ativa
  useEffect(() => {
    analytics.trackPageView('/');

    const startTime = Date.now();
    const heartbeatInterval = setInterval(() => {
      const elapsedSeconds = Math.round((Date.now() - startTime) / 1000);
      analytics.trackDwellTime(elapsedSeconds);
    }, 60000);

    return () => {
      clearInterval(heartbeatInterval);
      const totalSeconds = Math.round((Date.now() - startTime) / 1000);
      if (totalSeconds >= 5) {
        // useBeacon: true — garante que o ping final sobreviva ao fechamento da aba
        analytics.trackDwellTime(totalSeconds, { useBeacon: true });
      }
    };
  }, []);

  // Telemetria: Rastrear visualização ao selecionar um ponto cultural
  useEffect(() => {
    if (selectedEvent) {
      analytics.trackEventView(selectedEvent);
    }
  }, [selectedEvent]);

  const handleIntroFinish = () => {
    setShowIntro(false);
    setShowWelcome(true);
  };

  const handleWelcomeFinish = (selectedMapType) => {
    setMapType(selectedMapType);
    localStorage.setItem('mapPreference', selectedMapType);
    localStorage.setItem('hasVisited', 'true');
    setShowWelcome(false);
  };

  const toggleMapType = () => {
    const newType = mapType === 'satellite' ? 'roadmap' : 'satellite';
    setMapType(newType);
    localStorage.setItem('mapPreference', newType);
    analytics.trackMapLayerChange(newType);
  };

  useEffect(() => {
    setCurrentMediaIndex(0);
    setMediaAspectRatio(16 / 9);
    setResolvedMediaUrls({});
    setIsImageLightboxOpen(false);
    setLightboxIndex(0);
  }, [selectedEvent]);

  // Se veio parâmetro via URL, seleciona evento após carregar o catálogo completo
  useEffect(() => {
    if (eventIdParam && allEvents.length > 0) {
      const targetId = eventIdParam.toString();
      const event = allEvents.find(e => e.id.toString() === targetId);
      if (event) {
        // Se o evento estiver restrito pelos filtros atuais, reseta para exibi-lo no mapa
        if (filters.municipio !== 'Todos' && event.municipio !== filters.municipio) {
          updateFilter('municipio', 'Todos');
        }
        if (filters.categoria !== 'Todas' && event.categoria !== filters.categoria) {
          updateFilter('categoria', 'Todas');
        }
        if (filters.searchTerm) {
          updateFilter('searchTerm', '');
        }
        setSelectedEvent(event);
      }
    }
  }, [eventIdParam, allEvents, filters.municipio, filters.categoria, filters.searchTerm, updateFilter]);


  const getMediaItems = (event) => {
    const items = [];
    if (event.videos && Array.isArray(event.videos)) {
      event.videos.forEach(url => { if (url) items.push({ type: 'video', url }); });
    }
    if (event.fotos && Array.isArray(event.fotos)) {
      event.fotos.forEach(url => {
        if (url) {
          let parsedUrl = url;
          if (typeof url === 'string' && url.trim().startsWith('{')) {
            try { parsedUrl = JSON.parse(url); } catch (e) { }
          }
          items.push({ type: 'image', url: parsedUrl });
        }
      });
    }
    if (items.length === 0) {
      if (event.video_url) items.push({ type: 'video', url: event.video_url });
      if (event.foto_url) {
        let parsedUrl = event.foto_url;
        if (typeof event.foto_url === 'string' && event.foto_url.trim().startsWith('{')) {
          try { parsedUrl = JSON.parse(event.foto_url); } catch (e) { }
        }
        items.push({ type: 'image', url: parsedUrl });
      }
    }
    return items;
  };

  const mediaItems = useMemo(() => {
    return selectedEvent ? getMediaItems(selectedEvent) : [];
  }, [selectedEvent]);

  // Lazy loading de Signed URL para a imagem atualmente em exibição no modal/lightbox
  useEffect(() => {
    const loadSignedUrl = async (index) => {
      const current = mediaItems[index];
      if (current && current.type === 'image') {
        let originalPath = current.url;
        if (typeof current.url === 'object' && current.url !== null) {
          const isMobile = window.innerWidth <= 640;
          const isPortrait = window.innerHeight > window.innerWidth;
          
          if (isMobile || isPortrait) {
            originalPath = current.url.vertical_lg || current.url.vertical_sm || current.url.horizontal_lg || current.url.horizontal_sm || current.url.original || Object.values(current.url)[0];
          } else {
            originalPath = current.url.horizontal_lg || current.url.horizontal_sm || current.url.vertical_lg || current.url.vertical_sm || current.url.original || Object.values(current.url)[0];
          }
        }

        if (originalPath && !resolvedMediaUrls[index]) {
          const signedUrl = await getSignedUrlOnDemand(originalPath);
          setResolvedMediaUrls(prev => ({ ...prev, [index]: signedUrl }));
        } else if (!originalPath) {
           setResolvedMediaUrls(prev => ({ ...prev, [index]: null }));
        }
      }
    };

    if (selectedEvent && mediaItems.length > 0) {
      loadSignedUrl(currentMediaIndex);
      if (isImageLightboxOpen) {
        loadSignedUrl(lightboxIndex);
      }
    }
  }, [currentMediaIndex, lightboxIndex, isImageLightboxOpen, mediaItems, resolvedMediaUrls, selectedEvent]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isImageLightboxOpen) {
        if (e.key === 'Escape') {
          setIsImageLightboxOpen(false);
        } else if (e.key === 'ArrowLeft') {
          setLightboxIndex(prev => (prev === 0 ? mediaItems.length - 1 : prev - 1));
        } else if (e.key === 'ArrowRight') {
          setLightboxIndex(prev => (prev === mediaItems.length - 1 ? 0 : prev + 1));
        }
      } else if (selectedEvent && e.key === 'Escape') {
        setSelectedEvent(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageLightboxOpen, selectedEvent, mediaItems.length]);

  useEffect(() => {
    const currentMedia = mediaItems[currentMediaIndex];
    if (currentMedia && currentMedia.type === 'video') {
      setMediaAspectRatio(16 / 9);
    }
  }, [currentMediaIndex, mediaItems]);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      setMediaAspectRatio(naturalWidth / naturalHeight);
    }
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    if (distance > minSwipeDistance) setCurrentMediaIndex(prev => prev === mediaItems.length - 1 ? 0 : prev + 1);
    if (distance < -minSwipeDistance) setCurrentMediaIndex(prev => prev === 0 ? mediaItems.length - 1 : prev - 1);
  };

  const onLightboxTouchStart = (e) => {
    setLightboxTouchEnd(null);
    setLightboxTouchStart(e.targetTouches[0].clientX);
  };
  const onLightboxTouchMove = (e) => {
    setLightboxTouchEnd(e.targetTouches[0].clientX);
  };
  const onLightboxTouchEnd = () => {
    if (!lightboxTouchStart || !lightboxTouchEnd) return;
    const distance = lightboxTouchStart - lightboxTouchEnd;
    const minSwipeDistance = 50;
    if (distance > minSwipeDistance) setLightboxIndex(prev => (prev === mediaItems.length - 1 ? 0 : prev + 1));
    if (distance < -minSwipeDistance) setLightboxIndex(prev => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  };

  return (
    <div className={`flex h-[100dvh] overflow-hidden relative transition-colors duration-300 ${theme === 'dark' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-900'}`}>
      <IntroModal isOpen={showIntro} onNext={handleIntroFinish} />
      <WelcomeModal isOpen={showWelcome} onSelect={handleWelcomeFinish} />
      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <ErrorReportModal 
        isOpen={errorReportOpen} 
        onClose={() => {
          setErrorReportOpen(false);
          setErrorReportContext('');
        }} 
        initialContext={errorReportContext}
      />

      {flashMessage && (
        <FlashMessage message={flashMessage.message} type={flashMessage.type} onClose={() => setFlashMessage(null)} />
      )}

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-20 md:hidden backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar Esquerda */}
      <div className={`
        fixed md:relative z-30 h-full
        ${sidebarOpen ? 'translate-x-0 w-[280px] sm:w-80' : '-translate-x-full md:translate-x-0 md:w-0'} 
        transition-all duration-300 flex flex-col border-r overflow-y-auto scrollbar-hide
        ${theme === 'dark' ? 'bg-slate-800 border-cyan-500/30' : 'bg-white border-slate-200 shadow-xl'}
      `}>
        <div className={`p-4 md:p-6 border-b flex flex-col items-center text-center relative ${theme === 'dark' ? 'border-cyan-500/30' : 'border-slate-100'}`}>
          <button
            onClick={() => setSidebarOpen(false)}
            className={`md:hidden absolute top-4 right-4 p-2.5 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${theme === 'dark' ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-500'}`}
          >
            <X className="w-5 h-5" />
          </button>
          <img src={logo} alt="Logo" className="h-16 md:h-20 w-auto mb-3 md:mb-4 drop-shadow-[0_0_10px_rgba(6,182,212,0.5)]" />
          <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Eventos Culturais</h2>
        </div>

        {/* Filtros Integrados com useEvents */}
        <div className={`p-4 border-b shrink-0 ${theme === 'dark' ? 'border-slate-700' : 'border-slate-100'}`}>
          <div className="relative">
            <Search className={`absolute left-3 top-3.5 w-4 h-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 min-h-[44px] border rounded-lg focus:outline-none focus:border-cyan-500 transition-colors ${theme === 'dark' ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
            />
          </div>
        </div>

        <div className={`p-4 border-b ${theme === 'dark' ? 'border-slate-700' : 'border-slate-100'}`}>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-cyan-400" />
            <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>Município</span>
          </div>
          <select
            value={filters.municipio}
            onChange={(e) => {
              const val = e.target.value;
              updateFilter('municipio', val);
              analytics.trackFilterChange('municipio', val, eventos.length);
              if (window.innerWidth < 768 && val !== 'Todos') setSidebarOpen(false);
            }}
            className={`w-full p-3 min-h-[44px] border rounded-lg focus:outline-none focus:border-cyan-500 transition-colors ${theme === 'dark' ? 'bg-slate-900 text-slate-300 border-slate-700' : 'bg-slate-50 text-slate-700 border-slate-200'}`}
          >
            <option value="Todos">Todos os Municípios</option>
            {Array.from(new Set([...MUNICIPIOS_REGIAO.map(m => m.nome), ...availableCounties])).sort().map(mun => (
              <option key={mun} value={mun}>{mun}</option>
            ))}
          </select>
        </div>

        <div className={`p-4 border-b shrink-0 ${theme === 'dark' ? 'border-slate-700' : 'border-slate-100'}`}>
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-cyan-400" />
            <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>Categorias</span>
          </div>
          <select
            value={filters.categoria}
            onChange={(e) => {
              const val = e.target.value;
              updateFilter('categoria', val);
              analytics.trackFilterChange('categoria', val, eventos.length);
            }}
            className={`w-full p-3 min-h-[44px] border rounded-lg focus:outline-none focus:border-cyan-500 transition-colors ${theme === 'dark' ? 'bg-slate-900 text-slate-300 border-slate-700' : 'bg-slate-50 text-slate-700 border-slate-200'}`}
          >
            <option value="Todas">Todas as Categorias</option>
            {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>

        {/* Lista */}
        <div className={`p-4 border-b shrink-0 ${theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
          <div className="flex justify-between items-center text-sm">
            <span className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>Resultados:</span>
            <span className="font-bold text-cyan-500 bg-cyan-500/10 px-2 py-0.5 rounded-full">{eventos.length}</span>
          </div>
        </div>
        
        {loading ? (
          <div className="p-4 text-center text-sm text-cyan-500 animate-pulse">Carregando eventos...</div>
        ) : (
          <div className="p-4 space-y-3 pb-20 md:pb-4">
            {eventos.map(evento => (
              <div
                key={evento.id}
                onClick={() => { setSelectedEvent(evento); if (window.innerWidth < 768) setSidebarOpen(false); }}
                className={`p-4 rounded-lg cursor-pointer transition-all border ${selectedEvent?.id === evento.id ? (theme === 'dark' ? 'bg-slate-700 border-cyan-500 shadow-lg' : 'bg-cyan-50 border-cyan-500 shadow-lg') : (theme === 'dark' ? 'bg-slate-900 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300')}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className={`font-semibold text-sm pr-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{evento.nome}</h3>
                </div>
                <div className={`flex items-center gap-2 text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                  <MapPin className="w-3 h-3" />
                  <span>{evento.local}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Área Principal */}
      <div className="flex-1 flex flex-col w-full">
        <header className={`border-b p-2 sm:p-3 md:p-4 z-10 transition-colors duration-300 ${theme === 'dark' ? 'bg-slate-800 border-cyan-500/30' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex flex-row items-center justify-between gap-2 md:gap-4">
            <div className="flex items-center gap-2 md:gap-4 shrink-0">
              <button aria-label="Menu" onClick={() => setSidebarOpen(!sidebarOpen)} className={`p-2 sm:p-2.5 rounded-lg ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
                {sidebarOpen ? <X className="w-5 h-5 md:w-6 md:h-6" /> : <Menu className="w-5 h-5 md:w-6 md:h-6" />}
              </button>
              <h1 className="text-base sm:text-lg md:text-2xl font-bold bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Mapa Cultural</h1>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 justify-end shrink-0">
              <div className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${theme === 'dark' ? 'bg-cyan-900/30 text-cyan-400' : 'bg-cyan-50 text-cyan-700'}`}>
                <MapIcon className="w-3.5 h-3.5" />
                {eventos.length} pontos encontrados
              </div>
              <Link
                to="/sobre"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  theme === 'dark'
                    ? 'bg-slate-800 text-cyan-400 hover:bg-slate-700 border border-cyan-500/30 shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 shadow-sm'
                }`}
                title="Conheça o projeto, pesquisa acadêmica e a equipe"
              >
                <Info className="w-3.5 h-3.5 text-cyan-500" />
                <span>Sobre</span>
              </Link>
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'bg-slate-700 text-yellow-400 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                title={theme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </header>

        {/* Mapa e Modais */}
        <div className={`flex-1 relative overflow-hidden ${theme === 'dark' ? 'bg-slate-950' : 'bg-slate-200'}`}>
          <div className="absolute inset-0">
            <MapWithEvents
              events={eventos}
              selectedEvent={selectedEvent}
              onEventSelect={setSelectedEvent}
              filterMunicipio={filters.municipio}
              filterCategory={filters.categoria}
              setOutOfBoundsIndicators={setOutOfBoundsIndicators}
              mapType={mapType}
              resetViewTrigger={resetViewTrigger}
              onZoomChange={setCurrentMapZoom}
              onInitialZoomSet={(z) => {
                setInitialMapZoom(z);
                setCurrentMapZoom(z);
              }}
            />
          </div>

          {/* Botão flutuante para voltar ao zoom inicial quando estiver com zoom grande */}
          {isZoomed && !selectedEvent && (
            <div className="absolute top-4 left-4 z-20">
              <button
                onClick={handleResetZoom}
                className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl shadow-xl backdrop-blur-md border text-xs sm:text-sm font-semibold transition-all duration-200 hover:scale-105 active:scale-95 ${
                  theme === 'dark'
                    ? 'bg-slate-800/95 border-cyan-500/40 text-cyan-400 hover:bg-slate-700 shadow-cyan-950/40'
                    : 'bg-white/95 border-slate-300 text-slate-800 hover:bg-slate-50 shadow-slate-300/50'
                }`}
                title="Voltar ao zoom inicial e visão panorâmica da região"
              >
                <RotateCcw className="w-4 h-4 text-cyan-500" />
                <span>Voltar ao zoom inicial</span>
              </button>
            </div>
          )}

          {/* Controles do Mapa no canto superior direito */}
          <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
            {!selectedEvent && (
              <>
                <button
                  onClick={toggleMapType}
                  aria-label="Alternar tipo de mapa"
                  title={mapType === 'satellite' ? 'Alternar para mapa de ruas' : 'Alternar para satélite'}
                  className={`backdrop-blur-sm border p-3 rounded-xl shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 ${
                    theme === 'dark' ? 'bg-slate-800/90 border-cyan-500/30 text-white hover:bg-slate-700' : 'bg-white/90 border-slate-300 text-slate-800 hover:bg-slate-100'
                  }`}
                >
                  {mapType === 'satellite' ? <MapIcon className="w-5 h-5 text-blue-500" /> : <Layers className="w-5 h-5 text-cyan-500" />}
                </button>

                <button
                  onClick={handleResetZoom}
                  aria-label="Voltar ao zoom inicial"
                  title="Voltar ao zoom inicial (visão panorâmica)"
                  className={`backdrop-blur-sm border p-3 rounded-xl shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 ${
                    isZoomed
                      ? (theme === 'dark' ? 'bg-cyan-950/90 border-cyan-400 text-cyan-400 ring-2 ring-cyan-500/30' : 'bg-cyan-50 border-cyan-500 text-cyan-600 ring-2 ring-cyan-400/30')
                      : (theme === 'dark' ? 'bg-slate-800/90 border-cyan-500/30 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white/90 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900')
                  }`}
                >
                  <RotateCcw className={`w-5 h-5 ${isZoomed ? 'text-cyan-400' : ''}`} />
                </button>
              </>
            )}
          </div>

          {/* Out of bounds indicators (New Dynamic Pin System) */}
          {outOfBoundsIndicators.map((indicator) => (
            <div
              key={indicator.id}
              className="absolute z-10 w-8 h-8 flex items-center justify-center transition-all duration-300 ease-out"
              style={{
                left: `${indicator.x}%`,
                top: `${indicator.y}%`,
                transform: `translate(-50%, -50%) rotate(${indicator.rotation}deg)`,
              }}
            >
              <div
                className={`backdrop-blur border-2 rounded-full p-1.5 animate-pulse cursor-pointer hover:scale-110 transition-transform ${theme === 'dark'
                    ? 'bg-slate-800/90 border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:bg-slate-700'
                    : 'bg-white/90 border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.3)] hover:bg-slate-50'
                  }`}
                onClick={() => {
                  const ev = eventos.find(e => e.id === indicator.eventId);
                  if (ev) {
                    setSelectedEvent(ev);
                    analytics.trackIndicatorClick(ev.id, ev.nome);
                  }
                }}
                title="Ir para o evento nesta direção"
              >
                <MapPin className="w-5 h-5 text-cyan-500" style={{ transform: `rotate(${-indicator.rotation}deg)` }} />
              </div>
            </div>
          ))}

          {/* Botões Flutuantes Inferiores (Reportar Erro e Feedback) */}
          <div className="absolute bottom-6 right-6 z-20 flex items-center gap-2.5 sm:gap-3">
            <button
              onClick={() => {
                setErrorReportContext('');
                setErrorReportOpen(true);
                analytics.trackEvent('error_report_modal_open', 'engagement', 'floating_button');
              }}
              className={`flex items-center gap-2 px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-2xl border shadow-2xl backdrop-blur-md transition-all duration-200 hover:scale-105 active:scale-95 ${
                theme === 'dark'
                  ? 'bg-slate-800/95 border-rose-500/40 text-rose-300 hover:bg-slate-700 shadow-rose-950/40'
                  : 'bg-white/95 border-rose-200 text-rose-700 hover:bg-rose-50 shadow-slate-300/60'
              }`}
              title="Encontrou algum problema ou bug no mapa? Reporte aqui"
            >
              <div className="bg-rose-500 text-white p-1.5 rounded-lg shadow-sm">
                <Bug className="w-4 h-4" />
              </div>
              <span className="text-xs sm:text-sm font-bold">Reportar Erro</span>
            </button>

            <button
              onClick={() => {
                setFeedbackOpen(true);
                analytics.trackEvent('feedback_modal_open', 'engagement', 'floating_button');
              }}
              className={`flex items-center gap-2 px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-2xl border shadow-2xl backdrop-blur-md transition-all duration-200 hover:scale-105 active:scale-95 ${
                theme === 'dark' 
                  ? 'bg-slate-800/90 border-cyan-500/30 text-white hover:bg-slate-700' 
                  : 'bg-white/90 border-slate-200 text-slate-800 hover:bg-slate-50'
              }`}
            >
              <div className="bg-cyan-500 text-white p-1.5 rounded-lg">
                <MessageSquare className="w-4 h-4" />
              </div>
              <span className="text-xs sm:text-sm font-bold">Feedback</span>
            </button>
          </div>

          {/* Painel de Detalhes do Local / Evento */}
          {selectedEvent && (
            <div className={`
              absolute top-4 right-4 z-40 flex flex-col backdrop-blur-xl rounded-2xl border shadow-2xl overflow-hidden transition-all duration-300
              w-[calc(100vw-2rem)] max-h-[85vh]
              md:w-[480px] lg:w-[560px] xl:w-[640px] md:max-h-none md:h-[calc(100%-2rem)] md:bottom-4
              ${theme === 'dark' ? 'bg-slate-800/95 border-cyan-500/30 text-white' : 'bg-white/95 border-slate-200 text-slate-900'}
            `}>
              {/* Media Section */}
              <div 
                className={`relative w-full shrink-0 group overflow-hidden cursor-pointer ${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-100'}`} 
                style={{ aspectRatio: mediaAspectRatio, maxHeight: '360px' }} 
                onTouchStart={onTouchStart} 
                onTouchMove={onTouchMove} 
                onTouchEnd={onTouchEnd}
                onClick={() => {
                  if (mediaItems.length > 0) {
                    setLightboxIndex(currentMediaIndex);
                    setIsImageLightboxOpen(true);
                  }
                }}
              >
                {mediaItems.length > 0 ? (
                  mediaItems[currentMediaIndex].type === 'video' ? (
                     <video src={mediaItems[currentMediaIndex].url} controls className="w-full h-full object-cover" />
                  ) : resolvedMediaUrls[currentMediaIndex] ? (
                    <img 
                      src={resolvedMediaUrls[currentMediaIndex]} 
                      loading="lazy" 
                      decoding="async"
                      alt={selectedEvent.nome} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                      onLoad={handleImageLoad} 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center animate-pulse"><span className="text-cyan-500">Carregando imagem...</span></div>
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><span className="text-2xl text-slate-400">Sem imagem</span></div>
                )}

                {/* Botão Ampliar Imagem */}
                {mediaItems.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxIndex(currentMediaIndex);
                      setIsImageLightboxOpen(true);
                    }}
                    className="absolute top-3 left-3 bg-black/60 hover:bg-black/85 text-white px-3 py-1.5 rounded-xl backdrop-blur-md text-xs font-semibold flex items-center gap-1.5 transition-all shadow-lg border border-white/20 z-10 hover:scale-105"
                    title="Clique para ver a imagem ampliada"
                  >
                    <Maximize2 className="w-3.5 h-3.5 text-cyan-400" />
                    <span>Ampliar</span>
                  </button>
                )}
                
                {/* Botão Fechar Painel */}
                <button 
                  onClick={(e) => { e.stopPropagation(); setSelectedEvent(null); }} 
                  className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/85 rounded-full text-white z-10 backdrop-blur-md border border-white/20 transition-all hover:scale-110 min-w-[36px] min-h-[36px] flex items-center justify-center"
                  title="Fechar detalhes"
                >
                  <X className="w-5 h-5" />
                </button>

                {mediaItems.length > 1 && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); setCurrentMediaIndex(prev => prev === 0 ? mediaItems.length - 1 : prev - 1); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full p-2 text-white transition-all shadow-lg min-w-[40px] min-h-[40px] flex items-center justify-center backdrop-blur-sm z-10"><ChevronLeft size={20} /></button>
                    <button onClick={(e) => { e.stopPropagation(); setCurrentMediaIndex(prev => prev === mediaItems.length - 1 ? 0 : prev + 1); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full p-2 text-white transition-all shadow-lg min-w-[40px] min-h-[40px] flex items-center justify-center backdrop-blur-sm z-10"><ChevronRight size={20} /></button>
                    
                    {/* Indicador de Bolinhas (Dots Carousel) */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 bg-black/40 px-2.5 py-1.5 rounded-full backdrop-blur-sm">
                      {mediaItems.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => { e.stopPropagation(); setCurrentMediaIndex(idx); }}
                          className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${idx === currentMediaIndex ? 'bg-cyan-400 w-3.5' : 'bg-white/60 hover:bg-white'}`}
                          aria-label={`Ir para imagem ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Informações detalhadas do ponto/evento */}
              <div className="p-5 md:p-7 overflow-y-auto scrollbar-hide space-y-5 flex-1">
                <div>
                  <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {(selectedEvent.categorias && selectedEvent.categorias.length > 0 
                      ? selectedEvent.categorias 
                      : [selectedEvent.categoria]
                    ).map((cat, idx) => {
                      const tagColor = getEventColor({ categoria: cat }) || selectedEvent.color || '#06b6d4';
                      return (
                        <span
                          key={idx}
                          className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border whitespace-nowrap transition-colors"
                          style={{
                            backgroundColor: `${tagColor}20`,
                            borderColor: `${tagColor}50`,
                            color: tagColor
                          }}
                        >
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </span>
                      );
                    })}
                  </div>

                  <h3 className={`text-xl md:text-2xl lg:text-3xl font-extrabold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                    {selectedEvent.nome}
                  </h3>

                  {selectedEvent.creditos_foto && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-400">
                      <Camera className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                      <span className="truncate italic">Créditos: {selectedEvent.creditos_foto}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className={`flex items-start gap-3 text-sm md:text-base p-3.5 rounded-xl border ${theme === 'dark' ? 'bg-slate-900/60 border-slate-700/60 text-slate-200' : 'bg-slate-100/80 border-slate-200 text-slate-700'}`}>
                    <MapPin className="w-5 h-5 text-cyan-500 shrink-0 mt-0.5" />
                    <span className="font-medium leading-snug">{selectedEvent.local}</span>
                  </div>

                  {selectedEvent.descricao && (
                    <div className="space-y-1.5">
                      <h4 className={`text-xs font-bold uppercase tracking-wider ${theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'}`}>Descrição</h4>
                      <p className={`text-sm md:text-base leading-relaxed ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                        {selectedEvent.descricao}
                      </p>
                    </div>
                  )}

                  {selectedEvent.detalhes && (
                    <div className="space-y-1.5">
                      <h4 className={`text-xs font-bold uppercase tracking-wider ${theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'}`}>Detalhes e Informações</h4>
                      <p className={`text-sm md:text-base leading-relaxed whitespace-pre-wrap ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                        {selectedEvent.detalhes}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2.5">
                    <a 
                      href={`https://www.google.com/maps/dir/?api=1&destination=${selectedEvent.lat},${selectedEvent.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-xl font-bold text-sm md:text-base transition-all shadow-lg hover:shadow-cyan-500/25 active:scale-[0.99]"
                    >
                      <MapIcon className="w-5 h-5" />
                      Traçar Rota no Google Maps
                    </a>
                    <button
                      onClick={handleResetZoom}
                      className={`flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-semibold text-sm border transition-all active:scale-[0.99] ${
                        theme === 'dark'
                          ? 'bg-slate-900/80 hover:bg-slate-800 text-cyan-400 border-cyan-500/40 hover:border-cyan-400'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300'
                      }`}
                      title="Fechar detalhes e voltar ao zoom inicial do mapa"
                    >
                      <RotateCcw className="w-4 h-4 text-cyan-500" />
                      <span>Zoom Inicial</span>
                    </button>
                  </div>
                </div>
                
                <div className={`pt-4 border-t ${theme === 'dark' ? 'border-slate-700/80' : 'border-slate-200'}`}>
                  <StarRating eventId={selectedEvent.id} 
                    currentRatingSum={selectedEvent.rating_sum || 0} 
                    currentRatingCount={selectedEvent.rating_count || 0}
                    onRate={(newSum, newCount) => {
                      const updates = { rating_sum: newSum, rating_count: newCount };
                      setSelectedEvent(prev => ({ ...prev, ...updates }));
                      updateEventLocally(selectedEvent.id, updates);
                    }} 
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => {
                        setErrorReportContext(`Ponto cultural: ${selectedEvent.nome} (${selectedEvent.local || ''})`);
                        setErrorReportOpen(true);
                        analytics.trackEvent('error_report_point_click', 'engagement', selectedEvent.nome);
                      }}
                      className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all ${
                        theme === 'dark'
                          ? 'text-slate-400 border-slate-700 hover:text-rose-400 hover:border-rose-500/40 hover:bg-slate-900/60'
                          : 'text-slate-500 border-slate-200 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50/50'
                      }`}
                      title="Encontrou alguma informação incorreta ou problema neste ponto?"
                    >
                      <Bug className="w-3.5 h-3.5 text-rose-500" />
                      <span>Informar erro neste ponto</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox / Ampliador de Mídia Fullscreen (Mobile e Desktop) */}
      {isImageLightboxOpen && mediaItems.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col justify-between p-4 sm:p-6 select-none animate-fadeIn"
          onClick={() => setIsImageLightboxOpen(false)}
        >
          {/* Header da Galeria Ampliada */}
          <div className="flex items-center justify-between z-10 w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <span className="text-cyan-400 text-xs sm:text-sm font-bold bg-cyan-950/80 border border-cyan-500/30 px-3 py-1 rounded-full backdrop-blur-sm">
                {lightboxIndex + 1} / {mediaItems.length}
              </span>
              <h4 className="text-white font-bold text-sm sm:text-base truncate max-w-[220px] sm:max-w-md">
                {selectedEvent?.nome}
              </h4>
            </div>

            <button 
              onClick={() => setIsImageLightboxOpen(false)} 
              className="p-2.5 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white border border-slate-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Fechar galeria ampliada"
              title="Fechar (Esc)"
            >
              <X className="w-6 h-6 text-slate-200" />
            </button>
          </div>

          {/* Área Principal da Imagem/Vídeo */}
          <div 
            className="relative flex-1 flex items-center justify-center my-4 overflow-hidden" 
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onLightboxTouchStart}
            onTouchMove={onLightboxTouchMove}
            onTouchEnd={onLightboxTouchEnd}
          >
            {mediaItems[lightboxIndex]?.type === 'video' ? (
              <video 
                src={mediaItems[lightboxIndex].url} 
                controls 
                autoPlay 
                className="max-h-[80vh] max-w-full rounded-xl shadow-2xl object-contain" 
              />
            ) : resolvedMediaUrls[lightboxIndex] ? (
              <img 
                src={resolvedMediaUrls[lightboxIndex]} 
                decoding="async"
                alt={`${selectedEvent?.nome} - Imagem ampliada ${lightboxIndex + 1}`} 
                className="max-h-[80vh] max-w-full rounded-xl shadow-2xl object-contain transition-transform duration-300"
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 text-cyan-400">
                <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Carregando imagem em alta definição...</span>
              </div>
            )}

            {/* Setas de navegação da galeria ampliada */}
            {mediaItems.length > 1 && (
              <>
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setLightboxIndex(prev => prev === 0 ? mediaItems.length - 1 : prev - 1); 
                  }} 
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-slate-900/80 hover:bg-slate-800 rounded-full p-3 text-white transition-all shadow-2xl border border-slate-700 min-w-[48px] min-h-[48px] flex items-center justify-center backdrop-blur-md z-20"
                  aria-label="Imagem anterior"
                  title="Anterior (Seta Esquerda)"
                >
                  <ChevronLeft size={26} />
                </button>

                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setLightboxIndex(prev => prev === mediaItems.length - 1 ? 0 : prev + 1); 
                  }} 
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-slate-900/80 hover:bg-slate-800 rounded-full p-3 text-white transition-all shadow-2xl border border-slate-700 min-w-[48px] min-h-[48px] flex items-center justify-center backdrop-blur-md z-20"
                  aria-label="Próxima imagem"
                  title="Próxima (Seta Direita)"
                >
                  <ChevronRight size={26} />
                </button>
              </>
            )}
          </div>

          {/* Footer da Galeria Ampliada */}
          <div className="flex flex-col items-center justify-center text-center z-10" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs sm:text-sm text-slate-300 font-medium max-w-lg truncate">
              {selectedEvent?.local}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapaCulturalPiaui;
